import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Claim popup → Rules: Business Rules and Customer Rules on @inavista/components. Same columns, labels, status pill
// and toolbar (Search, Excel export for AllGrid_Toolbar_Excel_Export, Clear filters) as the Syncfusion grids they
// replace. Rows are the claim's own rules (the popup's form), served by LocalRowsGridApi; nothing is fetched.

export const BUSINESS_RULES_URL = 'page://claim-business-rules';
export const CUSTOMER_RULES_URL = 'page://claim-customer-rules';

export const BUSINESS_RULES_COLUMNS: [string, string][] = [
  ['__rowKey', 'int'],
  ['rule', 'nvarchar'],
  ['status', 'nvarchar'],
];

export const CUSTOMER_RULES_COLUMNS: [string, string][] = [
  ['__rowKey', 'int'],
  ['id', 'int'],
  ['ruleLine', 'nvarchar'],
  ['status', 'nvarchar'],
];

export const RULE_PASSED_ACTION = 'passed';
export const RULE_FAILED_ACTION = 'failed';

/** The page's grid context: the Excel rule, and the status pill colour (CommonService.getClaimsRuleStatus, as before). */
export interface RulesGridContext {
  canExport: boolean;
  statusColor: (value: unknown) => string;
}

function rulesGridBase(ctx: RulesGridContext): Partial<GridConfig> {
  const canExport = ctx.canExport;
  return {
    clientSide: true,
    keyColumn: '__rowKey',
    rowHeight: 26,
    hiddenByDefaultColumns: ['__rowKey'],
    columnColors: { status: ctx.statusColor },
    // No column chooser, reordering or paging before; the old grids were 300 px tall.
    enableColumnsPicker: false,
    enableExport: canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    stretchColumns: true,
    emptyStateMessage: 'No rules for this claim.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
  };
}

export function buildBusinessRulesGridConfig(ctx: RulesGridContext): GridConfig {
  return {
    ...rulesGridBase(ctx),
    apiBaseUrl: BUSINESS_RULES_URL,
    defaultColumnOrder: ['rule', 'status'],
    columnLabels: { rule: 'Rule', status: 'Status' },
    columnWidths: { rule: 300, status: 150 },
    columnSizing: { status: { noStretch: true } },
    // The old grid filtered every column with the checkbox (Excel) list.
    excelFilterColumns: ['rule', 'status'],
    persistenceKey: 'rims.service.claim-popup-business-rules',
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  } as GridConfig;
}

export function buildCustomerRulesGridConfig(ctx: RulesGridContext): GridConfig {
  return {
    ...rulesGridBase(ctx),
    apiBaseUrl: CUSTOMER_RULES_URL,
    defaultColumnOrder: ['ruleLine', 'status'],
    hiddenByDefaultColumns: ['__rowKey', 'id'],
    columnLabels: { ruleLine: 'Description', status: 'Status' },
    columnWidths: { ruleLine: 300, status: 150 },
    columnSizing: { status: { noStretch: true } },
    // Description had the menu filter, Status the checkbox (Excel) list.
    excelFilterColumns: ['status'],
    persistenceKey: 'rims.service.claim-popup-customer-rules',
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  } as GridConfig;
}

const CHECK_ICON = '<polyline points="20 6 9 17 4 12" />';
const CROSS_ICON = '<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />';

// Passed / Failed, as before: usable by a CSA on a claim In Progress; otherwise shown disabled, now with the reason.
export function buildCustomerRulesActionIcons(lockReason: () => string | null): ActionIconDef[] {
  const off = (_: GridRow) => !!lockReason();
  const why = (label: string) => (_: GridRow) => lockReason() ?? label;
  return [
    { key: RULE_PASSED_ACTION, label: 'Passed', icon: CHECK_ICON, colorFn: () => '#28a745', disabledFn: off, labelFn: why('Passed') },
    { key: RULE_FAILED_ACTION, label: 'Failed', icon: CROSS_ICON, colorFn: () => '#dc3545', disabledFn: off, labelFn: why('Failed') },
  ];
}
